import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { PhoneCall, UserCheck, Presentation, ThumbsDown, FileText, Plus, X, Play, Pause, Circle, RotateCcw } from 'lucide-react';
import { PANIC_QUOTES } from './panicQuotes';
import { DoomsdayClock } from './DoomsdayClock';

const COLORS = ['#007AFF','#22c55e','#FF3B30','#FF8C00','#6A0DAD','#e91e63','#00bcd4','#8bc34a','#FFD700','#666'];

const CHAT_USERNAMES = [
  'SalesKing99', 'CloserMike', 'GrindMode_', 'DealHunter', 'ColdCallChamp',
  'HustleHard24', 'RevenueRick', 'PhoneBeast', 'PipelinePro', 'DialKing',
  'NoExcuses_', 'OwnerWhisperer', 'DemoMachine', 'SalesGOAT', 'CallCrusher',
  'MrCloser', 'DialDemon', 'PitchPerfect', 'HungrMike', 'MoneyMoves_',
  'StreetSales', 'CaptainClose', 'SmashQuota', 'AlwaysBeCalling', 'TopProducer',
  'PhoneWarrior', 'SalesNinja_', 'DealStacker', 'HustleBoss', 'RingRingWin',
];
const CHAT_COLORS = ['#FF6B6B','#4ECDC4','#FFD93D','#6BCB77','#4D96FF','#FF922B','#CC5DE8','#20C997','#FF8787','#74C0FC','#F783AC','#A9E34B','#FCC419','#DA77F2','#63E6BE'];

const IDLE_MESSAGES = [
  'keep dialing, every no is closer to a yes',
  'you got this, one more call',
  'the grind never stops',
  'future you will thank present you',
  'pain is temporary, commission is forever',
  'dial dial dial',
  'somebody is about to say yes',
  'the phone is your money printer',
  'one call can change everything',
  'stay locked in',
  'no breaks, just breakthroughs',
  'your competition is sleeping rn',
  'next call could be the one',
  'outwork everyone',
  'imagine hitting goal early...',
  'lets gooo keep pushing',
  'the hustle is real',
  'owners need what you have, just call',
  'every dial = money in the bank',
  'you miss 100% of the calls you dont make',
  'focus mode activated',
  'this is how legends are built',
  'the pipeline fills itself when you work',
  'trust the process',
  'volume negates luck',
  'be the person who never gives up',
  'W after W after W',
  'cold calling builds empires',
];
const ACTION_MESSAGES = {
  spoke: [
    'LETS GOOOO spoke to the owner!!', 'W W W owner on the line!', 'thats what Im talking about',
    'owner picked up, its happening', 'another owner conversation LOCKED', 'ez money, great connect',
    'the owner listened, thats a WIN', 'building rapport one call at a time', 'OWNER CALL SECURED',
  ],
  demo: [
    'DEMO SET LETS GOOOOO', 'NO WAYYY DEMO!!!', 'THE DEMO MACHINE STRIKES AGAIN',
    'poggers demo set!!', 'thats a BIG W right there', 'MONEY INCOMING demo booked',
    'you are on FIRE', 'demo after demo this is insane', 'CLUTCH DEMO SET',
  ],
  notint: [
    'on to the next one', 'their loss honestly', 'shake it off, next!',
    'not interested today, maybe tomorrow', 'nbd, just keep dialing', 'cant win em all, keep going',
  ],
  custom: [
    'nice one, logged it!', 'keep tracking everything', 'data is power, good log',
    'another one for the books', 'logged and locked',
  ],
};

function buildMilestones(totalCalls) {
  if (totalCalls <= 0) return [[1, 5, 10, 15, 20, 25, 50, 75, 100, 125]];
  const milestones = [];
  const count = 40;
  for (let i = 0; i < count; i++) {
    const val = Math.max(1, Math.round(((i + 1) / count) * totalCalls));
    if (milestones.length === 0 || milestones[milestones.length - 1] !== val) milestones.push(val);
  }
  if (milestones[0] !== 1) milestones.unshift(1);
  if (milestones[milestones.length - 1] !== totalCalls) milestones.push(totalCalls);
  const rows = [];
  for (let i = 0; i < milestones.length; i += 10) rows.push(milestones.slice(i, i + 10));
  return rows;
}

function formatTimer(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

export const CallingMode = ({
  callsMade, callValue, goalRevenue, goalDeadline,
  exitCallMode, setCallsMade, apiUrl, token,
}) => {
  const [showScript, setShowScript] = useState(false);
  const [callScript, setCallScript] = useState(() => localStorage.getItem('call_script') || '');
  const [panicQuote, setPanicQuote] = useState(null);
  const [spokeCount, setSpokeCount] = useState(() => parseInt(localStorage.getItem('spoke_count') || '0'));
  const [demoCount, setDemoCount] = useState(() => parseInt(localStorage.getItem('demo_count') || '0'));
  const [notIntCount, setNotIntCount] = useState(() => parseInt(localStorage.getItem('notint_count') || '0'));
  const [thermoGoal, setThermoGoal] = useState(0);
  const [thermoCurrent, setThermoCurrent] = useState(0);
  const [thermoDailyLog, setThermoDailyLog] = useState([]);
  const [thermoAddInput, setThermoAddInput] = useState('');
  const [ownerCalls, setOwnerCalls] = useState(() => parseInt(localStorage.getItem('owner_calls_lifetime') || '0'));
  const [customButtons, setCustomButtons] = useState(() => { try { return JSON.parse(localStorage.getItem('custom_call_buttons') || '[]'); } catch { return []; } });
  const [customCounts, setCustomCounts] = useState(() => { try { return JSON.parse(localStorage.getItem('custom_call_counts') || '{}'); } catch { return {}; } });
  const [showAddBtn, setShowAddBtn] = useState(false);
  const [newBtnName, setNewBtnName] = useState('');
  const [newBtnColor, setNewBtnColor] = useState('#FF8C00');
  const [campaignName, setCampaignName] = useState(() => localStorage.getItem('campaign_name') || '50 Deals in 50 States');

  // Sub-style alert
  const [alert, setAlert] = useState(null);
  const alertTimer = useRef(null);

  // Fake viewer count
  const [viewers, setViewers] = useState(() => 847 + Math.floor(Math.random() * 200));
  const viewerRef = useRef(null);

  // Timer
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(() => parseInt(localStorage.getItem('call_timer') || '0'));
  const timerRef = useRef(null);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const chatRef = useRef(null);
  const chatIntervalRef = useRef(null);
  const saveTimer = useRef(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Timer logic
  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          const next = prev + 1;
          localStorage.setItem('call_timer', String(next));
          return next;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerRunning]);

  // Auto chat messages
  const addChat = useCallback((type = 'idle') => {
    const msgs = type === 'idle' ? IDLE_MESSAGES : (ACTION_MESSAGES[type] || IDLE_MESSAGES);
    const msg = msgs[Math.floor(Math.random() * msgs.length)];
    const user = CHAT_USERNAMES[Math.floor(Math.random() * CHAT_USERNAMES.length)];
    const color = CHAT_COLORS[Math.floor(Math.random() * CHAT_COLORS.length)];
    setChatMessages(prev => [...prev.slice(-50), { id: Date.now() + Math.random(), user, color, msg }]);
  }, []);

  useEffect(() => {
    // Start with a few messages
    for (let i = 0; i < 4; i++) setTimeout(() => addChat('idle'), i * 400);
    chatIntervalRef.current = setInterval(() => addChat('idle'), 3000 + Math.random() * 4000);
    return () => clearInterval(chatIntervalRef.current);
  }, [addChat]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chatMessages]);

  // Backend sync
  useEffect(() => {
    if (!apiUrl || !token) return;
    fetch(`${apiUrl}/api/admin/calling-session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if ((data.callsMade || 0) > callsMade) {
          setCallsMade(data.callsMade || 0); localStorage.setItem('calls_made', String(data.callsMade || 0));
          setSpokeCount(data.spokeCount || 0); localStorage.setItem('spoke_count', String(data.spokeCount || 0));
          setDemoCount(data.demoCount || 0); localStorage.setItem('demo_count', String(data.demoCount || 0));
          setNotIntCount(data.notIntCount || 0); localStorage.setItem('notint_count', String(data.notIntCount || 0));
          if (data.customCounts) { setCustomCounts(data.customCounts); localStorage.setItem('custom_call_counts', JSON.stringify(data.customCounts)); }
        }
        if ((data.ownerCalls || 0) > parseInt(localStorage.getItem('owner_calls_lifetime') || '0')) {
          setOwnerCalls(data.ownerCalls); localStorage.setItem('owner_calls_lifetime', String(data.ownerCalls));
        }
        if (data.customButtons && data.customButtons.length > 0 && customButtons.length === 0) {
          setCustomButtons(data.customButtons); localStorage.setItem('custom_call_buttons', JSON.stringify(data.customButtons));
        }
      }).catch(() => {});
  }, []);

  const saveToBackend = useCallback(() => {
    if (!apiUrl || !token) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const cm = parseInt(localStorage.getItem('calls_made') || '0');
      const sc = parseInt(localStorage.getItem('spoke_count') || '0');
      const dc = parseInt(localStorage.getItem('demo_count') || '0');
      const ni = parseInt(localStorage.getItem('notint_count') || '0');
      const oc = parseInt(localStorage.getItem('owner_calls_lifetime') || '0');
      let cc = {}; try { cc = JSON.parse(localStorage.getItem('custom_call_counts') || '{}'); } catch {}
      let cb = []; try { cb = JSON.parse(localStorage.getItem('custom_call_buttons') || '[]'); } catch {}
      fetch(`${apiUrl}/api/admin/calling-session`, {
        method: 'PUT', headers,
        body: JSON.stringify({ callsMade: cm, spokeCount: sc, demoCount: dc, notIntCount: ni, customCounts: cc, customButtons: cb, ownerCalls: oc }),
      }).catch(() => {});
    }, 1000);
  }, [apiUrl, token]);

  // Thermometer load
  useEffect(() => {
    if (!apiUrl || !token) return;
    fetch(`${apiUrl}/api/admin/thermometer`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.goal) setThermoGoal(data.goal);
        if (data.current) setThermoCurrent(data.current);
        if (data.daily_log) setThermoDailyLog(data.daily_log);
      }).catch(() => {});
  }, [apiUrl, token]);

  const saveThermometer = useCallback((goal, current, log) => {
    fetch(`${apiUrl}/api/admin/thermometer`, {
      method: 'PUT', headers,
      body: JSON.stringify({ goal, current, daily_log: log }),
    }).catch(() => {});
  }, [apiUrl, token]);

  const addDailyCalls = (count) => {
    if (!count || count <= 0) return;
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const newCurrent = thermoCurrent + count;
    const existingIdx = thermoDailyLog.findIndex(d => d.date === today);
    let newLog;
    if (existingIdx >= 0) {
      newLog = [...thermoDailyLog];
      newLog[existingIdx] = { ...newLog[existingIdx], calls: newLog[existingIdx].calls + count };
    } else {
      newLog = [...thermoDailyLog, { date: today, calls: count }];
    }
    setThermoCurrent(newCurrent);
    setThermoDailyLog(newLog);
    saveThermometer(thermoGoal, newCurrent, newLog);
  };

  const setThermoGoalValue = (val) => {
    setThermoGoal(val);
    saveThermometer(val, thermoCurrent, thermoDailyLog);
  };

  const resetThermometer = () => {
    setThermoCurrent(0);
    setThermoDailyLog([]);
    saveThermometer(thermoGoal, 0, []);
  };


  const triggerPanic = () => { const q = PANIC_QUOTES[Math.floor(Math.random() * PANIC_QUOTES.length)]; setPanicQuote(q); setTimeout(() => setPanicQuote(null), 5000); };

  // Sub-style alert system
  const ALERT_MESSAGES = {
    spoke: [
      { title: 'OWNER ON THE LINE', sub: 'Another decision-maker reached', color: '#007AFF' },
      { title: 'SPOKE TO OWNER', sub: 'The gatekeepers couldn\'t stop you', color: '#007AFF' },
      { title: 'DIRECT CONTACT', sub: 'Owner conversation locked in', color: '#007AFF' },
      { title: 'OWNER CONNECTED', sub: 'This is how deals start', color: '#007AFF' },
    ],
    demo: [
      { title: 'DEMO SET!!!', sub: 'Money is on the way', color: '#22c55e' },
      { title: 'DEMO BOOKED', sub: 'Another one in the pipeline', color: '#22c55e' },
      { title: 'LET\'S GOOO', sub: 'Demo secured, closing time soon', color: '#22c55e' },
      { title: 'DEMO MACHINE', sub: 'They can\'t say no to the demo', color: '#22c55e' },
    ],
    notint: [
      { title: 'NEXT!', sub: 'On to bigger and better', color: '#FF3B30' },
      { title: 'THEIR LOSS', sub: 'They\'ll wish they said yes', color: '#FF3B30' },
    ],
    custom: [
      { title: 'LOGGED', sub: 'Every action counts', color: '#FF8C00' },
    ],
    milestone: [
      { title: '10 OWNER CALLS!', sub: 'You\'re just getting warmed up', color: '#FFD700' },
      { title: '25 OWNER CALLS!', sub: 'Quarter mark hit, keep grinding', color: '#FFD700' },
      { title: '50 OWNER CALLS!', sub: 'HALFWAY TO LEGEND STATUS', color: '#FFD700' },
      { title: '100 OWNER CALLS!', sub: 'TRIPLE DIGITS — UNSTOPPABLE', color: '#FFD700' },
      { title: '250 OWNER CALLS!', sub: 'YOU ARE BUILT DIFFERENT', color: '#FFD700' },
      { title: '500 OWNER CALLS!', sub: 'ABSOLUTE MACHINE MODE', color: '#FFD700' },
    ],
  };
  const MILESTONES = [10, 25, 50, 100, 250, 500];

  const showAlert = useCallback((type) => {
    const msgs = ALERT_MESSAGES[type] || ALERT_MESSAGES.custom;
    const msg = msgs[Math.floor(Math.random() * msgs.length)];
    clearTimeout(alertTimer.current);
    setAlert(msg);
    alertTimer.current = setTimeout(() => setAlert(null), 3000);
  }, []);

  const checkMilestoneAlert = useCallback((newCount) => {
    const hit = MILESTONES.find(m => newCount === m);
    if (hit) {
      const msg = ALERT_MESSAGES.milestone.find(m => m.title.includes(String(hit)));
      if (msg) { clearTimeout(alertTimer.current); setAlert(msg); alertTimer.current = setTimeout(() => setAlert(null), 4000); }
    }
  }, []);

  // Fake viewer count — fluctuates, spikes on actions
  useEffect(() => {
    viewerRef.current = setInterval(() => {
      setViewers(prev => prev + Math.floor(Math.random() * 11) - 5); // drift +-5
    }, 5000);
    return () => clearInterval(viewerRef.current);
  }, []);
  const spikeViewers = useCallback(() => {
    setViewers(prev => prev + Math.floor(Math.random() * 30) + 10);
  }, []);

  const incrementCalls = () => {
    setCallsMade(prev => { const next = prev + 1; localStorage.setItem('calls_made', String(next)); return next; });
    setOwnerCalls(prev => { const next = prev + 1; localStorage.setItem('owner_calls_lifetime', String(next)); return next; });
  };

  const handleSpokeToOwner = () => { setSpokeCount(p => { const n=p+1; localStorage.setItem('spoke_count',String(n)); return n; }); incrementCalls(); addChat('spoke'); showAlert('spoke'); spikeViewers(); checkMilestoneAlert(callsMade + 1); saveToBackend(); };
  const handleDemo = () => { setDemoCount(p => { const n=p+1; localStorage.setItem('demo_count',String(n)); return n; }); incrementCalls(); addChat('demo'); showAlert('demo'); spikeViewers(); checkMilestoneAlert(callsMade + 1); saveToBackend(); };
  const handleNotInterested = () => { setNotIntCount(p => { const n=p+1; localStorage.setItem('notint_count',String(n)); return n; }); incrementCalls(); addChat('notint'); showAlert('notint'); saveToBackend(); if (window.__doomsdayDecrement) window.__doomsdayDecrement(); };
  const handleCustomClick = (btnId) => { const u={...customCounts,[btnId]:(customCounts[btnId]||0)+1}; setCustomCounts(u); localStorage.setItem('custom_call_counts',JSON.stringify(u)); incrementCalls(); addChat('custom'); showAlert('custom'); spikeViewers(); saveToBackend(); };

  const addCustomButton = () => { if(!newBtnName.trim()) return; const id=`custom_${Date.now()}`; const btn={id,name:newBtnName.trim(),color:newBtnColor}; const u=[...customButtons,btn]; setCustomButtons(u); localStorage.setItem('custom_call_buttons',JSON.stringify(u)); setNewBtnName(''); setNewBtnColor('#FF8C00'); setShowAddBtn(false); saveToBackend(); };
  const removeCustomButton = (id) => { const u=customButtons.filter(b=>b.id!==id); setCustomButtons(u); localStorage.setItem('custom_call_buttons',JSON.stringify(u)); const cc={...customCounts}; delete cc[id]; setCustomCounts(cc); localStorage.setItem('custom_call_counts',JSON.stringify(cc)); saveToBackend(); };

  const handleReset = () => {
    if (!window.confirm('Reset ALL calling progress? This cannot be undone.')) return;
    setCallsMade(0); localStorage.setItem('calls_made','0');
    setSpokeCount(0); localStorage.setItem('spoke_count','0');
    setDemoCount(0); localStorage.setItem('demo_count','0');
    setNotIntCount(0); localStorage.setItem('notint_count','0');
    setCustomCounts({}); localStorage.setItem('custom_call_counts','{}');
    setTimerSeconds(0); localStorage.setItem('call_timer','0');
    setTimerRunning(false);
    saveToBackend();
  };

  const totalActions = spokeCount + demoCount + notIntCount + Object.values(customCounts).reduce((s,v) => s+v, 0);
  const cv = parseFloat(callValue || '0');
  const goal = parseFloat(goalRevenue || '0');
  const callsNeeded = cv > 0 && goal > 0 ? Math.ceil(goal / cv) : 0;
  const milestoneRows = useMemo(() => buildMilestones(callsNeeded), [callsNeeded]);
  const pctComplete = callsNeeded > 0 ? Math.min(100, (callsMade / callsNeeded) * 100) : 0;

  // Goal deadline
  let daysLeft = null;
  if (goalDeadline) {
    const today = new Date(); today.setHours(0,0,0,0);
    const deadline = new Date(goalDeadline + 'T00:00:00');
    if (!isNaN(deadline.getTime())) daysLeft = Math.max(0, Math.ceil((deadline - today) / 86400000));
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#0a0a0a', display: 'flex', flexDirection: 'column', fontFamily: 'inherit' }}>
      {/* TOP BAR - Twitch style */}
      <div style={{ background: '#18181b', borderBottom: '2px solid #2a2a2e', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Live indicator + viewers */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Circle size={10} fill="#FF3B30" color="#FF3B30" style={{ animation: timerRunning ? 'pulse 1.5s infinite' : 'none' }} />
          <span style={{ color: timerRunning ? '#FF3B30' : '#666', fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase' }}>
            {timerRunning ? 'LIVE' : 'OFFLINE'}
          </span>
          <span style={{ color: '#555', margin: '0 0.15rem' }}>|</span>
          <span style={{ color: '#aaa', fontWeight: 700, fontSize: '0.75rem' }}>
            {viewers.toLocaleString()} watching
          </span>
        </div>

        {/* Campaign name */}
        <input
          data-testid="campaign-name"
          value={campaignName}
          onChange={(e) => { setCampaignName(e.target.value); localStorage.setItem('campaign_name', e.target.value); }}
          style={{ background: 'transparent', border: 'none', color: '#efeff1', fontFamily: "'Bangers', cursive", fontSize: '1.2rem', letterSpacing: '0.03em', outline: 'none', width: '250px' }}
          placeholder="Campaign Name..."
        />

        {/* Timer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button data-testid="timer-toggle" onClick={() => setTimerRunning(prev => !prev)}
            style={{ width: '32px', height: '32px', borderRadius: '50%', background: timerRunning ? '#FF3B30' : '#22c55e', border: '2px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
            {timerRunning ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: '2px' }} />}
          </button>
          <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', color: '#efeff1', letterSpacing: '0.08em', minWidth: '100px' }}>
            {formatTimer(timerSeconds)}
          </div>
          <button data-testid="timer-reset" onClick={() => { setTimerRunning(false); setTimerSeconds(0); localStorage.setItem('call_timer', '0'); }}
            title="Start over"
            style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#333', border: '1px solid #555', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#aaa', fontSize: '0.7rem', fontWeight: 900, transition: 'all 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FF8C00'; e.currentTarget.style.color = '#000'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#333'; e.currentTarget.style.color = '#aaa'; }}>
            <RotateCcw size={12} />
          </button>
        </div>

        {/* Goal banner removed - now a scrolling ticker below */}

        {/* Earnings */}
        <div data-testid="call-earnings" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto' }}>
          <div style={{ padding: '3px 10px', background: '#FFD70020', border: '1px solid #FFD700', borderRadius: '4px', fontWeight: 800, fontSize: '0.75rem', color: '#FFD700' }}>
            {callsMade.toLocaleString()} calls
          </div>
          <span style={{ color: '#555' }}>=</span>
          <div style={{ padding: '3px 10px', background: '#22c55e', borderRadius: '4px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#000' }}>
            ${(callsMade * cv).toFixed(2)}
          </div>
          <button data-testid="reset-session-btn" onClick={handleReset}
            style={{ padding: '4px 10px', background: '#FF8C00', color: '#000', border: 'none', borderRadius: '4px', fontWeight: 800, fontSize: '0.7rem', cursor: 'pointer', textTransform: 'uppercase' }}>Reset</button>
          <button onClick={exitCallMode}
            style={{ padding: '4px 10px', background: '#FF3B30', color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 800, fontSize: '0.7rem', cursor: 'pointer', textTransform: 'uppercase' }}>Exit</button>
        </div>
      </div>

      {/* Doomsday Clock */}
      <DoomsdayClock token={token} />

      {/* Scrolling Goal Ticker Banner */}
      {goal > 0 && (() => {
        const earned = (callsMade * cv).toFixed(2);
        const tickerText = `${callsMade.toLocaleString()} / ${callsNeeded.toLocaleString()} OWNER CALLS \u00a0\u00a0\u00a0 ${callsMade} OWNERS = $${earned} OF $${goal.toLocaleString()} \u00a0\u00a0\u00a0 ${daysLeft !== null ? daysLeft + ' DAYS LEFT' : ''} \u00a0\u00a0\u00a0 ${Math.round(pctComplete)}% COMPLETE \u00a0\u00a0\u00a0 ${campaignName.toUpperCase()}`;
        return (
          <div style={{ background: '#FFD700', overflow: 'hidden', whiteSpace: 'nowrap', borderBottom: '2px solid #000' }}>
            <div style={{ display: 'inline-block', animation: 'tickerScroll 25s linear infinite', paddingLeft: '100%' }}>
              <span style={{ fontFamily: "'Bangers', cursive", fontSize: '1.1rem', color: '#000', letterSpacing: '0.06em' }}>
                {tickerText} {'\u00a0\u00a0\u00a0\u00a0\u00a0'} {tickerText}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Script */}
      <div style={{ background: '#18181b', borderBottom: showScript ? '1px solid #6A0DAD' : 'none' }}>
        <button data-testid="toggle-script-btn" onClick={() => setShowScript(p => !p)}
          style={{ width: '100%', padding: '0.3rem 1rem', background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', color: showScript ? '#b57aff' : '#555', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>
          <FileText size={12} /> {showScript ? 'Hide Script' : 'Script'} {callScript ? '●' : ''}
        </button>
        {showScript && (
          <div style={{ padding: '0 1rem 0.5rem' }}>
            <textarea data-testid="call-script-input" value={callScript}
              onChange={(e) => { setCallScript(e.target.value); localStorage.setItem('call_script', e.target.value); }}
              placeholder="Your call script..." rows={4}
              style={{ width: '100%', padding: '0.5rem', background: '#0e0e10', border: '1px solid #333', borderRadius: '6px', color: '#ddd', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        )}
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* LEFT PANEL — Actions */}
        <div style={{ width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', overflow: 'auto', background: '#0e0e10', borderRight: '1px solid #2a2a2e' }}>
          {/* Session Stats */}
          <div data-testid="session-tracker" style={{ background: '#18181b', border: '2px solid #2a2a2e', borderRadius: '10px', padding: '0.75rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', justifyContent: 'center', marginBottom: '0.4rem' }}>
              {[
                { label: 'Owner', count: spokeCount, color: '#007AFF' },
                { label: 'Demo', count: demoCount, color: '#22c55e' },
                { label: 'Not Int.', count: notIntCount, color: '#FF3B30' },
                ...customButtons.map(b => ({ label: b.name, count: customCounts[b.id] || 0, color: b.color })),
              ].map((item, i) => (
                <div key={i} style={{ textAlign: 'center', padding: '0.25rem 0.5rem', background: `${item.color}15`, border: `1px solid ${item.color}`, borderRadius: '6px', minWidth: '50px' }}>
                  <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.3rem', color: item.color }}>{item.count}</div>
                  <div style={{ fontSize: '0.5rem', fontWeight: 800, color: item.color, textTransform: 'uppercase' }}>{item.label}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '0.2rem', background: '#0e0e10', borderRadius: '4px', fontSize: '0.75rem' }}>
              <span style={{ fontFamily: "'Bangers', cursive", color: '#FFD700' }}>{totalActions}</span>
              <span style={{ color: '#555', margin: '0 0.3rem' }}>|</span>
              <span style={{ fontFamily: "'Bangers', cursive", color: '#22c55e' }}>${(callsMade * cv).toFixed(2)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <button data-testid="action-spoke-owner" onClick={handleSpokeToOwner}
            style={{ width: '100%', padding: '1rem', background: '#007AFF', color: '#fff', border: '3px solid #000', borderRadius: '10px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', textTransform: 'uppercase', boxShadow: '4px 4px 0px 0px #000', transition: 'all 0.12s', fontFamily: "'Bangers', cursive", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(4px,4px)'; e.currentTarget.style.boxShadow = 'none'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '4px 4px 0px 0px #000'; }}>
            <UserCheck size={20} /> Spoke to Owner
          </button>
          <button data-testid="action-demo" onClick={handleDemo}
            style={{ width: '100%', padding: '1rem', background: '#22c55e', color: '#000', border: '3px solid #000', borderRadius: '10px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', textTransform: 'uppercase', boxShadow: '4px 4px 0px 0px #000', transition: 'all 0.12s', fontFamily: "'Bangers', cursive", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(4px,4px)'; e.currentTarget.style.boxShadow = 'none'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '4px 4px 0px 0px #000'; }}>
            <Presentation size={20} /> Demo Set
          </button>
          <button data-testid="action-not-interested" onClick={handleNotInterested}
            style={{ width: '100%', padding: '1rem', background: '#FF3B30', color: '#fff', border: '3px solid #000', borderRadius: '10px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', textTransform: 'uppercase', boxShadow: '4px 4px 0px 0px #000', transition: 'all 0.12s', fontFamily: "'Bangers', cursive", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(4px,4px)'; e.currentTarget.style.boxShadow = 'none'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '4px 4px 0px 0px #000'; }}>
            <ThumbsDown size={20} /> Not Interested
          </button>

          {customButtons.map(btn => (
            <div key={btn.id} style={{ position: 'relative' }}>
              <button onClick={() => handleCustomClick(btn.id)}
                style={{ width: '100%', padding: '1rem', background: btn.color, color: '#fff', border: '3px solid #000', borderRadius: '10px', fontWeight: 900, fontSize: '1rem', cursor: 'pointer', textTransform: 'uppercase', boxShadow: '4px 4px 0px 0px #000', transition: 'all 0.12s', fontFamily: "'Bangers', cursive" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translate(4px,4px)'; e.currentTarget.style.boxShadow = 'none'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '4px 4px 0px 0px #000'; }}>
                {btn.name}
              </button>
              <button onClick={() => removeCustomButton(btn.id)} style={{ position: 'absolute', top: '-4px', right: '-4px', width: '18px', height: '18px', borderRadius: '50%', background: '#000', border: '1px solid #555', color: '#aaa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, fontSize: '0.6rem' }}>
                <X size={10} />
              </button>
            </div>
          ))}

          {!showAddBtn ? (
            <button data-testid="add-custom-btn" onClick={() => setShowAddBtn(true)}
              style={{ width: '100%', padding: '0.5rem', background: 'transparent', color: '#555', border: '1px dashed #333', borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
              <Plus size={14} /> Add Button
            </button>
          ) : (
            <div style={{ background: '#18181b', border: '1px solid #FFD700', borderRadius: '8px', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <input data-testid="custom-btn-name" type="text" value={newBtnName} onChange={(e) => setNewBtnName(e.target.value)} placeholder="Button name..." autoFocus
                style={{ width: '100%', padding: '0.4rem', background: '#0e0e10', border: '1px solid #333', borderRadius: '4px', color: '#fff', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: '0.2rem', flexWrap: 'wrap' }}>
                {COLORS.map(c => <button key={c} onClick={() => setNewBtnColor(c)} style={{ width: '22px', height: '22px', borderRadius: '50%', background: c, border: newBtnColor === c ? '2px solid #fff' : '1px solid #333', cursor: 'pointer' }} />)}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button data-testid="save-custom-btn" onClick={addCustomButton} disabled={!newBtnName.trim()}
                  style={{ flex: 1, padding: '0.4rem', background: newBtnName.trim() ? '#FFD700' : '#333', color: '#000', border: 'none', borderRadius: '4px', fontWeight: 800, fontSize: '0.7rem', cursor: newBtnName.trim() ? 'pointer' : 'not-allowed' }}>Create</button>
                <button onClick={() => { setShowAddBtn(false); setNewBtnName(''); }}
                  style={{ padding: '0.4rem 0.75rem', background: '#222', color: '#aaa', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>X</button>
              </div>
            </div>
          )}

          <button data-testid="panic-btn" onClick={triggerPanic}
            style={{ width: '100%', padding: '0.5rem', background: 'transparent', color: '#FF3B30', border: '1px solid #FF3B30', borderRadius: '8px', fontWeight: 900, fontSize: '0.8rem', cursor: 'pointer', textTransform: 'uppercase', fontFamily: "'Bangers', cursive", transition: 'all 0.15s', marginTop: 'auto' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FF3B30'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#FF3B30'; }}>
            PANIC
          </button>
        </div>

        {/* CENTER — Call Tracker Grid */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0.75rem', overflow: 'auto', background: '#0e0e10' }}>
          <div data-testid="call-milestone-grid" style={{ flex: 1, background: '#18181b', border: '2px solid #2a2a2e', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.3rem', color: '#FFD700', marginBottom: '0.25rem', textAlign: 'center' }}>
              {callsNeeded > 0 ? `Goal: ${callsNeeded.toLocaleString()} Owner Calls` : 'Call Tracker'}
            </div>
            {callsNeeded > 0 && (
              <div style={{ marginBottom: '0.75rem', padding: '0 0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#22c55e' }}>{callsMade.toLocaleString()} made</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#FF8C00' }}>{Math.max(0, callsNeeded - callsMade).toLocaleString()} left</span>
                </div>
                <div style={{ height: '8px', background: '#222', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pctComplete}%`, height: '100%', borderRadius: '4px', background: pctComplete >= 100 ? '#22c55e' : pctComplete >= 50 ? '#FF8C00' : '#FFD700', transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            <div style={{ flex: 1 }}>
              {milestoneRows.map((row, ri) => (
                <div key={ri} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${row.length}, 1fr)`, gap: '2px' }}>
                    {row.map(m => {
                      const hit = callsMade >= m;
                      return (
                        <div key={m} style={{ display: 'flex', flexDirection: 'column', border: `2px solid ${hit ? '#22c55e' : '#2a2a2e'}`, borderRadius: '3px', overflow: 'hidden', transition: 'all 0.3s' }}>
                          <div style={{ width: '100%', height: '32px', background: hit ? '#22c55e' : '#18181b', transition: 'background 0.3s' }} />
                          <div style={{ width: '100%', padding: '2px 0', textAlign: 'center', fontSize: '0.55rem', fontWeight: 800, color: hit ? '#22c55e' : '#555', background: hit ? '#22c55e15' : '#0e0e10', borderTop: `1px solid ${hit ? '#22c55e' : '#2a2a2e'}` }}>
                            {m >= 1000 ? `${(m/1000).toFixed(1)}K` : m}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', fontFamily: "'Bangers', cursive", fontSize: '1.3rem', color: pctComplete >= 100 ? '#FFD700' : pctComplete >= 75 ? '#FF8C00' : pctComplete >= 25 ? '#22c55e' : '#333' }}>
              {pctComplete >= 100 ? 'GOAL CRUSHED!' : pctComplete >= 75 ? 'ALMOST THERE!' : pctComplete >= 50 ? 'HALFWAY!' : pctComplete >= 25 ? 'KEEP GOING!' : ''}
            </div>
            {/* Owner Calls Counter */}
            <div data-testid="lifetime-counter" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.75rem 0 0.25rem', marginTop: '0.5rem', borderTop: '1px solid #2a2a2e' }}>
              <button data-testid="owner-calls-down" onClick={() => { if(ownerCalls<=0) return; const n=ownerCalls-1; setOwnerCalls(n); localStorage.setItem('owner_calls_lifetime',String(n)); saveToBackend(); }}
                style={{ width: '34px', height: '34px', borderRadius: '6px', background: '#18181b', border: '1px solid #2a2a2e', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 900 }}
                onMouseEnter={(e) => { e.currentTarget.style.background='#FF3B30'; e.currentTarget.style.color='#fff'; }} onMouseLeave={(e) => { e.currentTarget.style.background='#18181b'; e.currentTarget.style.color='#666'; }}>▼</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '2.5rem', color: '#efeff1', letterSpacing: '0.1em', lineHeight: 1 }}>{String(ownerCalls).padStart(4,'0')}</div>
                <div style={{ fontSize: '0.6rem', fontWeight: 800, color: '#666', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Owner Calls</div>
              </div>
              <button data-testid="owner-calls-up" onClick={() => { const n=ownerCalls+1; setOwnerCalls(n); localStorage.setItem('owner_calls_lifetime',String(n)); saveToBackend(); }}
                style={{ width: '34px', height: '34px', borderRadius: '6px', background: '#18181b', border: '1px solid #2a2a2e', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 900 }}
                onMouseEnter={(e) => { e.currentTarget.style.background='#22c55e'; e.currentTarget.style.color='#000'; }} onMouseLeave={(e) => { e.currentTarget.style.background='#18181b'; e.currentTarget.style.color='#666'; }}>▲</button>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL — Call Thermometer */}
        <div style={{ width: '300px', flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#18181b', borderLeft: '1px solid #2a2a2e' }}>
          <div style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #2a2a2e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#efeff1', textTransform: 'uppercase' }}>Call Goal</span>
            <span style={{ fontSize: '0.65rem', color: '#22c55e', fontWeight: 800 }}>
              {thermoGoal > 0 ? `${Math.min(100, Math.round((thermoCurrent / thermoGoal) * 100))}%` : 'Set goal'}
            </span>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0.75rem', gap: '0.75rem' }}>
            {/* Goal Input */}
            <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input type="number" min={1} placeholder="Goal" value={thermoGoal || ''}
                onChange={e => setThermoGoalValue(parseInt(e.target.value) || 0)}
                style={{ flex: 1, padding: '6px 8px', background: '#0e0e10', border: '1.5px solid #333', borderRadius: '5px', color: '#FFD700', fontSize: '0.85rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
              <span style={{ fontSize: '0.65rem', color: '#666', fontWeight: 600 }}>calls</span>
            </div>

            {/* Thermometer Visual */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0.5rem 0' }}>
              <div style={{ position: 'relative', width: '60px', height: '100%', minHeight: '200px' }}>
                {/* Tube */}
                <div style={{ position: 'absolute', bottom: '15px', left: '50%', transform: 'translateX(-50%)', width: '36px', height: 'calc(100% - 30px)', background: '#0e0e10', borderRadius: '18px', border: '3px solid #333', overflow: 'hidden' }}>
                  {/* Fill */}
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: thermoGoal > 0 ? `${Math.min(100, (thermoCurrent / thermoGoal) * 100)}%` : '0%',
                    background: thermoCurrent >= thermoGoal && thermoGoal > 0 ? 'linear-gradient(to top, #22c55e, #4ade80)' : 'linear-gradient(to top, #FF3B30, #FF8C00, #FFD700)',
                    borderRadius: '0 0 14px 14px',
                    transition: 'height 0.5s ease-out',
                  }} />
                  {/* Tick marks */}
                  {[25, 50, 75].map(p => (
                    <div key={p} style={{ position: 'absolute', bottom: `${p}%`, left: 0, right: 0, height: '1px', background: '#333' }} />
                  ))}
                </div>
                {/* Bulb */}
                <div style={{
                  position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
                  width: '48px', height: '48px', borderRadius: '50%',
                  background: thermoCurrent >= thermoGoal && thermoGoal > 0 ? '#22c55e' : '#FF3B30',
                  border: '3px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: thermoCurrent >= thermoGoal && thermoGoal > 0 ? '0 0 20px rgba(34,197,94,0.5)' : '0 0 15px rgba(255,59,48,0.3)',
                }}>
                  <span style={{ fontFamily: "'Bangers', cursive", fontSize: '0.75rem', color: '#fff' }}>
                    {thermoCurrent}
                  </span>
                </div>
                {/* Percentage label */}
                {thermoGoal > 0 && (
                  <div style={{
                    position: 'absolute',
                    bottom: `calc(${Math.min(95, Math.max(20, (thermoCurrent / thermoGoal) * 100))}% - 10px)`,
                    left: '68px', whiteSpace: 'nowrap',
                    fontFamily: "'Bangers', cursive", fontSize: '1.5rem',
                    color: thermoCurrent >= thermoGoal ? '#22c55e' : '#FFD700',
                    transition: 'bottom 0.5s',
                  }}>
                    {Math.min(100, Math.round((thermoCurrent / thermoGoal) * 100))}%
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.5rem', background: '#0e0e10', borderRadius: '6px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.25rem', color: '#FFD700' }}>{thermoCurrent}</div>
                <div style={{ fontSize: '0.55rem', color: '#666', fontWeight: 600 }}>DONE</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.25rem', color: '#FF3B30' }}>{Math.max(0, thermoGoal - thermoCurrent)}</div>
                <div style={{ fontSize: '0.55rem', color: '#666', fontWeight: 600 }}>LEFT</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.25rem', color: '#22c55e' }}>{thermoGoal}</div>
                <div style={{ fontSize: '0.55rem', color: '#666', fontWeight: 600 }}>GOAL</div>
              </div>
            </div>

            {/* Add Today's Calls */}
            <div>
              <div style={{ fontSize: '0.65rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>Add Today's Calls</div>
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                <input type="number" min={1} placeholder="0" value={thermoAddInput}
                  onChange={e => setThermoAddInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { addDailyCalls(parseInt(thermoAddInput) || 0); setThermoAddInput(''); } }}
                  style={{ flex: 1, padding: '6px 8px', background: '#0e0e10', border: '1.5px solid #22c55e50', borderRadius: '5px', color: '#22c55e', fontSize: '0.85rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
                <button onClick={() => { addDailyCalls(parseInt(thermoAddInput) || 0); setThermoAddInput(''); }}
                  style={{ padding: '6px 12px', background: '#22c55e', color: '#000', border: 'none', borderRadius: '5px', fontWeight: 800, fontSize: '0.7rem', cursor: 'pointer', textTransform: 'uppercase' }}>
                  Add
                </button>
              </div>
            </div>

            {/* Daily Log */}
            {thermoDailyLog.length > 0 && (
              <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>Daily Log</div>
                {[...thermoDailyLog].reverse().map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0.4rem', fontSize: '0.7rem', borderBottom: '1px solid #1a1a1a' }}>
                    <span style={{ color: '#888' }}>{d.date}</span>
                    <span style={{ color: '#22c55e', fontWeight: 800 }}>+{d.calls}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Reset */}
            <button onClick={resetThermometer}
              style={{ padding: '5px 10px', background: 'none', border: '1.5px solid #333', borderRadius: '5px', color: '#666', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>
              Reset Progress
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes chatFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes panicPulse { 0% { opacity:0; transform:scale(0.8); } 20% { opacity:1; transform:scale(1.05); } 30% { transform:scale(0.98); } 40% { transform:scale(1); } 90% { opacity:1; } 100% { opacity:0; transform:scale(0.95); } }
        @keyframes tickerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes alertSlideIn { 0% { opacity:0; transform:translateY(-60px) scale(0.7); } 15% { opacity:1; transform:translateY(10px) scale(1.05); } 25% { transform:translateY(0) scale(1); } 85% { opacity:1; transform:translateY(0) scale(1); } 100% { opacity:0; transform:translateY(-30px) scale(0.9); } }
        @keyframes alertGlow { 0%,100% { box-shadow: 0 0 20px rgba(255,215,0,0.3); } 50% { box-shadow: 0 0 50px rgba(255,215,0,0.7); } }
      `}</style>

      {/* Sub-style Alert Overlay */}
      {alert && (
        <div style={{
          position: 'fixed', top: '15%', left: 0, right: 0, zIndex: 10002,
          display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          animation: 'alertSlideIn 3s ease-out forwards',
        }}>
          <div style={{
            background: '#18181b', border: `4px solid ${alert.color}`, borderRadius: '16px',
            padding: '1.5rem 3rem', textAlign: 'center', maxWidth: '600px',
            boxShadow: `0 0 40px ${alert.color}60, 0 0 80px ${alert.color}30`,
            animation: 'alertGlow 1s ease-in-out infinite',
          }}>
            <div style={{
              fontFamily: "'Bangers', cursive", fontSize: '3rem', color: alert.color,
              letterSpacing: '0.06em', lineHeight: 1.1, textShadow: `2px 2px 0px #000, 0 0 20px ${alert.color}80`,
            }}>
              {alert.title}
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#aaa', marginTop: '0.5rem', letterSpacing: '0.03em' }}>
              {alert.sub}
            </div>
          </div>
        </div>
      )}

      {panicQuote && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'panicPulse 5s ease-in-out forwards', padding: '2rem' }}
          onClick={() => setPanicQuote(null)}>
          <div style={{ maxWidth: '600px', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Bangers', cursive", fontSize: '2.5rem', color: '#FFD700', lineHeight: 1.2, marginBottom: '1.5rem', textShadow: '3px 3px 0px #000' }}>"{panicQuote.text}"</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#FF3B30', textTransform: 'uppercase' }}>— {panicQuote.by}</div>
            <div style={{ marginTop: '2rem', fontSize: '0.8rem', color: '#555' }}>tap anywhere to dismiss</div>
          </div>
        </div>
      )}
    </div>
  );
};
